import { Link, TableCell, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  createSearchRequestProvider,
  getActiveFiltersCount,
  preprocessSearchRequest,
  withoutBaseTableModel,
} from "~/components/Table";
import {
  commonResourceColumns,
  selectCountableListResponse,
} from "~/domain/common";
import type { Ingestion, LqsColumn } from "~/lqs";
import { LqsResourceTable, useIngestions } from "~/lqs";
import { useLqsNavigator } from "~/paths";
import { listIngestionsSchema } from "../schemas";

function IngestionNameCell({ ingestion }: { ingestion: Ingestion }) {
  const lqsNavigator = useLqsNavigator({ toIngestionDetails: true });

  return (
    <TableCell>
      {ingestion.name ?? "-"}
      <Link
        component={RouterLink}
        to={lqsNavigator.toIngestionDetails({ ingestionId: ingestion.id })}
      >
        <Typography variant="body2">{ingestion.id}</Typography>
      </Link>
    </TableCell>
  );
}

const columns: ReadonlyArray<LqsColumn<"ingestion">> = [
  {
    header: "Ingestion",
    sortKey: "name",
    renderCell: (ingestion) => <IngestionNameCell ingestion={ingestion} />,
  },
  {
    accessor: "logId",
    dataType: "foreign-key",
    resourceType: "log",
  },
  {
    accessor: "objectStoreId",
    dataType: "foreign-key",
    resourceType: "object-store",
  },
  {
    accessor: "objectKey",
    dataType: "text",
    isSortable: true,
    defaultHidden: true,
  },
  {
    accessor: "state",
    dataType: "text",
    isSortable: true,
  },
  {
    accessor: "progress",
    dataType: "percent",
    isSortable: true,
  },
  {
    accessor: "transitionedAt",
    dataType: "datetime",
    isSortable: true,
    defaultHidden: true,
  },
  {
    accessor: "workflowId",
    dataType: "foreign-key",
    resourceType: "workflow",
  },
  {
    accessor: "locked",
    dataType: "boolean",
    isSortable: true,
    defaultHidden: true,
  },
  {
    accessor: "lockedBy",
    dataType: "foreign-key",
    resourceType: "user",
    defaultHidden: true,
  },
  {
    accessor: "lockedAt",
    dataType: "datetime",
    isSortable: true,
    defaultHidden: true,
  },
  {
    accessor: "note",
    dataType: "text",
    isSortable: true,
  },
  ...commonResourceColumns,
];

export const [useIngestionSearchRequest, IngestionSearchRequestProvider] =
  createSearchRequestProvider({
    columns,
    filterSchema: listIngestionsSchema,
  });

export function IngestionTable() {
  const [request, setRequest] = useIngestionSearchRequest();

  const lqsNavigator = useLqsNavigator({ toIngestionCreate: true });

  const searchQuery = useIngestions(preprocessSearchRequest(request), {
    keepPreviousData: true,
    cacheTime: 0,
    select: selectCountableListResponse,
  });

  const filterValues = withoutBaseTableModel(request);

  return (
    <LqsResourceTable
      resourceName="ingestion"
      registryKey="ingestion"
      resourceCreateLocation={lqsNavigator.toIngestionCreate()}
      getRowKey={(ingestion) => ingestion.id}
      columns={columns}
      searchQuery={searchQuery}
      tableModel={request}
      onTableModelChange={setRequest}
      activeFilterCount={getActiveFiltersCount(filterValues)}
    />
  );
}
